import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef } from 'react';

import { Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { EmptyState, Loading } from '~/components';
import { RelativeTime } from '~/elements';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardToken, CardTokenRepository } from '~/repositories/CardTokenRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

export const PageCardTokensOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [currentPlatform?.id, isSandbox, resetPagination]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['card-tokens', 'list', currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () => CardTokenRepository.listCardTokens({ limit: pageSize, startingAfter: cursor }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
  });
  const cardTokens = data?.cardTokens ?? [];
  const hasMore = Boolean(data?.hasMore);

  const columns: TableColumn<CardToken>[] = [
    {
      header: 'Token ID',
      accessor: 'id',
      width: { min: 'min-content', max: '1fr' },
      cell: ({ value }) => (
        <Table.TextCell displayVariant="monospaced" weight="medium" value={(value as string) || '-'} />
      ),
    },
    {
      header: 'Card ID',
      accessor: 'cardId',
      width: { min: 'min-content', max: '1fr' },
      cell: ({ value }) => <Table.TextCell displayVariant="monospaced" value={(value as string) || '-'} />,
    },
    {
      header: 'Wallet Type',
      accessor: 'walletType',
      width: { min: 'min-content', max: '1fr' },
      cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
    },
    {
      header: 'Token Type',
      accessor: 'tokenType',
      width: { min: 'min-content', max: '1fr' },
      cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
    },
    {
      header: 'Device Name',
      accessor: 'deviceName',
      width: { min: 'min-content', max: '1fr' },
      cell: ({ value }) => <Table.TextCell value={(value as string) || '-'} />,
    },
    {
      header: 'Status',
      accessor: 'status',
      width: { min: 'min-content', max: 'min-content' },
      cell: ({ value }) => <Table.ChipCell value={value ? formatString(value as string) : '-'} />,
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      width: { min: 'min-content', max: 'min-content' },
      cell: ({ value }) => <Table.Cell>{value ? <RelativeTime timestamp={value as Date} /> : '-'}</Table.Cell>,
    },
  ];

  return (
    <>
      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          {isError ? (
            <EmptyState headline="Error" text="Failed to load card tokens." />
          ) : cardTokens.length === 0 && !hasPrevious ? (
            <EmptyState headline="No card tokens" text="No card tokens found." />
          ) : (
            <Table<CardToken>
              columns={columns}
              data={cardTokens}
              variant={TableVariant.List}
              pagination={{
                hasNext: hasMore,
                hasPrevious,
                pageIndex,
                pageSize,
                onNext: () => {
                  if (cardTokens.length > 0) {
                    onNext({ cursor: cardTokens[cardTokens.length - 1].id });
                  }
                },
                onPrevious,
                onPageSizeChange: setPageSize,
              }}
              onRowClick={(row) => {
                navigate(`${ROUTE.CARD_TOKENS}/edit/${row.id}`);
              }}
            />
          )}
        </Inner>
      </Loading>
    </>
  );
};
